import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash/noop';
import _isEqual from 'lodash/isEqual';

import { getBestBannerSize } from './helpers/getBestBannerSize';
import detectAdBlocker from './helpers/detectAdBlocker';

const url = 'https://www.googletagservices.com/tag/js/gpt.js';

class GdnBanner extends Component {
  static propTypes = {
    className: PropTypes.string,
    containerId: PropTypes.string.isRequired,
    bannerId: PropTypes.string.isRequired,
    lang: PropTypes.arrayOf(PropTypes.string).isRequired,
    size: PropTypes.shape({
      type: PropTypes.oneOf(['fixed', 'fluid']).isRequired,
      width: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
    refreshTimeout: PropTypes.number,
    sizeMapping: PropTypes.array,
    refreshOnOptionsChange: PropTypes.bool,
    bannerOptions: function(props, propName, componentName) {
      const bannerOptions = props[propName];
      let inValidKey = '';
      const isInValid = Object.keys(bannerOptions).some(key => {
        if (Array.isArray(bannerOptions[key])) {
          return false;
        }
        inValidKey = key;
        return true;
      });
      if (isInValid) {
        return new Error(
          'Invalid prop `' +
            `${propName}.${bannerOptions[inValidKey]}` +
            '` of type `' +
            typeof bannerOptions[inValidKey] +
            '` supplied to `' +
            componentName +
            '`, expected `arrayOfStrings`.'
        );
      }
    },
    onRender: PropTypes.func,
    onDestroy: PropTypes.func,
  };
  static defaultProps = {
    bannerOptions: {},
    onRender: _noop,
    onDestroy: _noop,
    refreshOnOptionsChange: false,
    refreshTimeout: 200,
  };

  static scriptElement;
  static scriptLoaded = false;

  onWindowResize = () => {};
  bannerBreakpoints = [];
  currentBannerSize = null;
  refreshTimeoutId = undefined;
  banner = null;

  componentDidMount() {
    if (!GdnBanner.scriptElement) {
      this.loadGdnScript();
    } else {
      this.waitLoading();
    }
  }

  // грохаем баннер (пока он находится в своем containerId) перед перерисовкой другого
  componentWillReceiveProps(nextProps) {
    if (
      this.props.containerId !== nextProps.containerId ||
      this.props.bannerId !== nextProps.bannerId
    ) {
      this.removeBanner();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.containerId !== prevProps.containerId ||
      this.props.bannerId !== prevProps.bannerId
    ) {
      // после render новым containerId/bannerId - перерисовываем
      if (GdnBanner.scriptLoaded) {
        if (!this.unmounted) {
          this.removeBanner();
        }

        this.createBanner();
      }
    } else if (
      this.props.refreshOnOptionsChange &&
      !_isEqual(this.props.bannerOptions, prevProps.bannerOptions)
    ) {
      // с опцией refreshOnOptionsChange = true, при условии что баннер уже создан
      // и bannerOptions отличаются, нужно пересоздать баннер
      this.removeBanner();
      this.createBanner();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.loadingTimer);
    if (!this.unmounted) {
      this.removeBanner();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !_isEqual(this.state, nextState) || !_isEqual(this.props, nextProps);
  }

  loadGdnScript = () => {
    GdnBanner.scriptElement = document.createElement('script');
    GdnBanner.scriptElement.setAttribute('src', url);
    GdnBanner.scriptElement.onload = () => {
      GdnBanner.scriptLoaded = true;
      if (!this.unmounted) {
        this.createBanner();
      }
    };
    GdnBanner.scriptElement.onerror = () => {
      GdnBanner.scriptLoaded = false;
      if (!this.unmounted) {
        this.removeBanner(true);
      }
    };
    document.head.appendChild(GdnBanner.scriptElement);
  };

  waitLoading = () => {
    if (GdnBanner.scriptLoaded) {
      this.createBanner();
      return;
    }
    this.loadingTimer = setTimeout(this.waitLoading, 200);
  };

  startWatchingWindowResize = () => {
    const { refreshTimeout } = this.props;
    this.onWindowResize = evt => {
      // on resize window and body resize speed might differ
      // thus width value is extracted from the actual content
      // part of the page, not the window's width
      const visibleWidth = document.body.clientWidth;
      const visibleHeight = evt.target.outerHeight;

      if (this.currentBannerSize) {
        const bestBannerSize = getBestBannerSize(this.bannerBreakpoints, [
          visibleWidth,
          visibleHeight,
        ]);

        if (bestBannerSize[0] !== this.currentBannerSize[0]) {
          this.currentBannerSize = [...bestBannerSize];
          clearTimeout(this.refreshTimeoutId);
          // prevents several banner request if several banner
          // breakpoints are switched within a second
          this.refreshTimeoutId = setTimeout(() => {
            googletag.pubads().refresh([this.banner]);
          }, refreshTimeout);
        }
      } else {
        this.currentBannerSize = getBestBannerSize(this.bannerBreakpoints, [
          visibleWidth,
          visibleHeight,
        ]);
      }
    };

    if (typeof window !== 'undefined') {
      this.currentBannerSize = getBestBannerSize(this.bannerBreakpoints, [
        document.body.clientWidth,
        window.outerHeight,
      ]);
      window.addEventListener('resize', this.onWindowResize);
    }
  };

  handleAdsBlocked = () => {
    if (!this.unmounted) {
      this.removeBanner(true);
    }
  };

  stopWatchingWindowResize = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize);
    }
  };

  getBannerSize = isMappingAdded => {
    const { size } = this.props;

    if (isMappingAdded) {
      return this.bannerBreakpoints;
    }

    return size.type === 'fluid' ? size.type : [size.width, size.height];
  };

  createBanner = () => {
    try {
      const { containerId, bannerId, lang, bannerOptions, sizeMapping = [], onRender } = this.props;

      const googletag = window.googletag || {};
      googletag.cmd = googletag.cmd || [];

      googletag.cmd.push(() => {
        let isMappingAdded = false;
        const sizeMappingObject = googletag.sizeMapping();

        this.bannerBreakpoints = sizeMapping.reduce((breakPoints, mapping) => {
          if (mapping.length === 2) {
            sizeMappingObject.addSize([...mapping[0]], [...mapping[1]]);
            isMappingAdded = true;
            breakPoints.push(mapping[0]);
          }
          return breakPoints;
        }, []);

        // Define instance of banner
        this.banner = googletag.defineSlot(
          bannerId,
          this.getBannerSize(isMappingAdded),
          containerId
        );

        if (!this.banner) {
          this.removeBanner();
          return;
        }

        this.banner.setCollapseEmptyDiv(true);

        this.unmounted = false;

        if (isMappingAdded) {
          this.banner.defineSizeMapping(sizeMappingObject.build());
          // Add 'resize' window event handler to change
          // banners without reloading the page
          this.startWatchingWindowResize(this.bannerBreakpoints);
        }

        this.banner.addService(googletag.pubads());

        // Add targeting settings
        googletag.pubads().enableSingleRequest();
        googletag.pubads().setTargeting('lang', lang);
        Object.keys(bannerOptions).forEach(option => {
          googletag.pubads().setTargeting(option, bannerOptions[option]);
        });
        // Enable GPT services
        googletag.enableServices();
        detectAdBlocker(this.handleAdsBlocked);

        googletag.pubads().addEventListener('slotRenderEnded', event => {
          if (this.banner === event.slot) {
            if (event.isEmpty) {
              this.removeBanner(event.isEmpty);
            } else {
              onRender();
            }
          }
        });
      });

      // Show banner on page
      googletag.cmd.push(() => {
        googletag.display(containerId);
      });
    } catch (e) {
      console.warn('gdn banner was blocked by adBlock -->', e);
    }
  };

  removeBanner = isEmptyOrBlocked => {
    try {
      const googletag = window.googletag || {};
      // We need to set the unmounted flag to true
      // even if a banner was not created
      this.unmounted = true;
      // Destroy instance only this banner
      if (this.banner && googletag.destroySlots) {
        this.unmounted = googletag.destroySlots([this.banner]);
        this.banner = null;
      }
      this.currentBannerSize = null;
      this.stopWatchingWindowResize();
      const { onDestroy } = this.props;
      onDestroy(isEmptyOrBlocked);
    } catch (e) {}
  };

  render() {
    const { containerId, size, className } = this.props;

    if (!containerId) {
      return null;
    }

    if (size.type === 'fluid') {
      return <div id={containerId} className={className} style={{ textAlign: 'center' }} />;
    }

    return (
      <div
        id={containerId}
        className={className}
        style={{ width: `${size.width}px`, height: `${size.height}px` }}
      />
    );
  }
}

export default GdnBanner;
